import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentPaste as PasteIcon, Clear as ClearIcon } from '@mui/icons-material';
import { validateSAMUrl, normalizeSAMUrl } from '../../utils/urlValidation';

interface SolicitationInputProps {
  onSubmit: (url: string) => Promise<void>;
  isLoading?: boolean;
}

export const SolicitationInput: React.FC<SolicitationInputProps> = ({
  onSubmit,
  isLoading = false
}) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateSAMUrl(url);
    if (!validation.isValid) {
      setError(validation.message || 'Invalid URL');
      return;
    }

    try {
      const normalizedUrl = normalizeSAMUrl(url);
      await onSubmit(normalizedUrl);
      setError(null);
    } catch (error) {
      setError('Failed to process the solicitation. Please try again.');
      console.error('Solicitation submission error:', error);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text);
      setError(null);
    } catch (error) {
      console.error('Failed to paste from clipboard:', error);
    }
  };

  const handleClear = () => {
    setUrl('');
    setError(null);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Analyze SAM.gov Solicitation
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Enter a SAM.gov opportunity URL to analyze the solicitation details
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="SAM.gov URL"
          variant="outlined"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (error) setError(null);
          }}
          error={!!error}
          helperText={error}
          placeholder="https://sam.gov/opp/..."
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {url && (
                  <Tooltip title="Clear">
                    <IconButton onClick={handleClear} edge="end" size="small">
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Paste from clipboard">
                  <IconButton onClick={handlePaste} edge="end" size="small">
                    <PasteIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!url.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Analyzing...' : 'Analyze Solicitation'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Example: https://sam.gov/opp/123456789abcdef/view
        </Typography>
      </Box>
    </Paper>
  );
};
